import React, { useState, useRef, useEffect } from "react";
import { CompanySelector } from "@/components/company/CompanySelector";
import { Company } from "@/types/company";
import { Card, CardContent } from "@/components/ui/card";
import { Search, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useCompany } from "@/hooks/useCompany";
import { CompanyFormDialog } from "@/components/company/CompanyFormDialog";
import {
  companyUILabels,
  companyValidationLabels,
  commonMiscLabels,
  commonFieldsLabels,
} from "@/i18n";

export interface CompanySelectorData {
  companyId?: string;
  company?: Company;
}

interface CompanySelectorSectionProps {
  value: CompanySelectorData;
  onChange: (data: CompanySelectorData, valid: boolean) => void;
  isAdminMode?: boolean; // When true, shows the full company list (admin view)
}

export const CompanySelectorSection: React.FC<CompanySelectorSectionProps> = ({
  value,
  onChange,
  isAdminMode = false,
}) => {
  // All hooks must be called unconditionally
  const [orgNumber, setOrgNumber] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false); // Track if user has actually searched
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<Company | undefined>(
    value.company
  );

  // Only use public company service in non-admin mode
  const { company, isLoadingCompanies, refetchCompanies } = useCompany(
    isAdminMode ? undefined : hasSearched ? searchQuery : undefined // Only search if user has clicked search
  );

  // i18n labels
  const uiLabels = companyUILabels();
  const validationLabels = companyValidationLabels();
  const miscLabels = commonMiscLabels();
  const fieldLabels = commonFieldsLabels();

  // Use ref to store onChange callback to avoid infinite loops
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Handle value changes from parent (for both modes)
  useEffect(() => {
    if (value.company && value.company._id !== selectedCompany?._id) {
      setSelectedCompany(value.company);
      if (!isAdminMode) {
        setOrgNumber(value.company.organisationNumber);
        setErrorMessage("");
      }
    }
  }, [value.company, selectedCompany?._id, isAdminMode]);

  // Handle search results (public mode only)
  useEffect(() => {
    // Early return if conditions aren't met
    if (isAdminMode || !searchQuery || !hasSearched) {
      if (!searchQuery) {
        setIsSearching(false);
      }
      return;
    }

    // Only set searching state if we have an active search query and loading
    if (isLoadingCompanies) {
      setIsSearching(true);
      return;
    }

    // When loading is done, process results
    setIsSearching(false);

    // Use the single company from org number search
    if (company) {
      // Only update if company changed
      if (selectedCompany?._id !== company._id) {
        setSelectedCompany(company);
        const newData: CompanySelectorData = {
          companyId: company._id,
          company: company,
        };
        onChangeRef.current(newData, true);
        setErrorMessage("");
      }
    } else {
      // No company found - only set error if we're not currently loading
      // This prevents setting error during retries
      if (!isLoadingCompanies) {
        // Only update if we had a company selected before
        if (selectedCompany) {
          setSelectedCompany(undefined);
          onChangeRef.current(
            { companyId: undefined, company: undefined },
            false
          );
          setErrorMessage(validationLabels.companyNotRegistered);
        } else if (!errorMessage) {
          // Only set error message if we don't already have one
          setErrorMessage(validationLabels.companyNotRegistered);
          onChangeRef.current(
            { companyId: undefined, company: undefined },
            false
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, company, isLoadingCompanies, isAdminMode, hasSearched]);

  // Admin mode: show full company selector
  if (isAdminMode) {
    const handleCompanySelect = (company: Company) => {
      setSelectedCompany(company);
      const newData: CompanySelectorData = {
        companyId: company._id,
        company: company,
      };
      onChangeRef.current(newData, true);
    };

    return (
      <Card>
        <CardContent className="pt-4">
          <CompanySelector
            selectedCompanyId={selectedCompany?._id}
            onCompanySelect={handleCompanySelect}
          />

          {selectedCompany && (
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">
                {uiLabels.selectedOrganization}:
              </h4>
              <div className="text-sm text-green-700 dark:text-green-300">
                <p>
                  <strong>{uiLabels.company}:</strong> {selectedCompany.name}
                </p>
                <p>
                  <strong>{uiLabels.organizationNumber}:</strong>{" "}
                  {selectedCompany.organisationNumber}
                </p>
                <p>
                  <strong>{fieldLabels.address}:</strong>{" "}
                  {selectedCompany.address}, {selectedCompany.postalNumber}{" "}
                  {selectedCompany.city}
                </p>
                <p>
                  <strong>{miscLabels.email}:</strong> {selectedCompany.email}
                </p>
                <p>
                  <strong>{miscLabels.phone}:</strong> {selectedCompany.phone}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Public mode: org number search

  const handleSearch = async () => {
    if (!orgNumber.trim()) {
      setErrorMessage(validationLabels.orgNumberRequired);
      return;
    }

    setErrorMessage("");
    setHasSearched(true);
    setSearchQuery(orgNumber.trim());
    // Note: setIsSearching will be handled by useEffect based on isLoadingCompanies
  };

  const handleCreateSuccess = async (createdCompany?: Company) => {
    setShowCreateDialog(false);

    if (createdCompany) {
      // Set the org number in the input field
      setOrgNumber(createdCompany.organisationNumber);
      setErrorMessage("");

      // Set the company directly so it's selected and displayed immediately
      setSelectedCompany(createdCompany);
      const newData: CompanySelectorData = {
        companyId: createdCompany._id,
        company: createdCompany,
      };
      onChangeRef.current(newData, true);

      // Also set search query for consistency (in case user searches again)
      setSearchQuery(createdCompany.organisationNumber);

      // Refetch companies in the background to ensure data is up to date
      await refetchCompanies();
    } else {
      await refetchCompanies();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleSearch();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder={validationLabels.invalidOrgNumber.split("(")[0].trim()}
            value={orgNumber}
            onChange={(e) => {
              setOrgNumber(e.target.value);
              setErrorMessage("");
              setSelectedCompany(undefined);
              setHasSearched(false); // Reset search state when user types
              onChangeRef.current(
                { companyId: undefined, company: undefined },
                false
              );
            }}
            onKeyDown={handleKeyDown}
            className="pl-10"
          />
        </div>
        <Button
          type="button"
          onClick={handleSearch}
          disabled={isSearching || (isLoadingCompanies && hasSearched)}
        >
          {isSearching || (isLoadingCompanies && hasSearched) ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {miscLabels.searching}
            </>
          ) : (
            <>
              <Search className="h-4 w-4 mr-2" />
              {miscLabels.search}
            </>
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowCreateDialog(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {errorMessage && (
        <div className="text-red-500 text-sm flex items-center gap-2">
          <span>{errorMessage}</span>
          <span className="text-gray-500">
            {validationLabels.registerNewCompany}
          </span>
        </div>
      )}

      {selectedCompany && (
        <div className="text-sm text-green-600 dark:text-green-400 font-medium">
          {selectedCompany.name}
        </div>
      )}

      <CompanyFormDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={handleCreateSuccess}
        initialOrgNumber={orgNumber}
      />
    </div>
  );
};
